import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
    Box,
    Grid,
    Typography,
    TextField,
    Button,
    Link,
    Autocomplete,
    Paper,
    Collapse,
    IconButton
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import theme from '../../../assets/theme';
import StyledTextField from '../../../components/UIComponents/StyledTextField';
import StyledAutocomplete from '../../../components/UIComponents/StyledAutocomplete';
import { fetchCountryList, fetchGuestDetailsByMobile, uploadFrappeMedia, addTag, fetchStateList, updateFileAttachment, getDocInfoComplete, getFileDetails, retrievefile } from '../../../service/network';
import { useLoading } from '../../../context/loading.context';
import { useNotification } from '../../../context/notification.context';
import InputAdornment from '@mui/material/InputAdornment';
import ErrorIcon from '@mui/icons-material/Error';
import CloseIcon from '@mui/icons-material/Close';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Chip from '@mui/material/Chip';
import UploadIcon from '@mui/icons-material/Upload';
import AttachmentIcon from '@mui/icons-material/Attachment';
import SaveIcon from '@mui/icons-material/Save';
const titles = ['Mr.', 'Ms.', 'Mrs.', 'Dr.'];
const genders = ['Male', 'Female', 'Trans'];
const idProofs = ['Aadhar Card', 'PAN Card', 'Passport', 'Driving License', 'Others'];

const GuestForm = React.memo(({ values, onChange, isEditable = true, onMobileNumberChange, onEmailChange, isPrimary = false, showRequiredErrors = false }) => {
    const [countries, setCountries] = useState([]);
    const [states, setStates] = useState([]); // Add states state

    const { showLoader, hideLoader } = useLoading();
    const { showNotification } = useNotification();
    const [attachmentPopupOpen, setAttachmentPopupOpen] = useState(false);
    const [selectedDocumentType, setSelectedDocumentType] = useState('');
    const [selectedFile, setSelectedFile] = useState(null);
    const [fileError, setFileError] = useState('');
    const [attachments, setAttachments] = useState(values.attachments || []);
   

    useEffect(() => {
        setAttachments(values.attachments);
    }, [values.attachments])

    // Add these helper functions
    const handleOpenAttachmentPopup = () => {
        setAttachmentPopupOpen(true);
        setSelectedDocumentType('');
        setSelectedFile(null);
        setFileError('');
    };

    const handleCloseAttachmentPopup = () => {
        setAttachmentPopupOpen(false);
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        // Check file size (5MB limit)
        if (file.size > 5 * 1024 * 1024) {
            setFileError('File size should not exceed 5MB');
            return;
        }

        // Check file type
        const validTypes = ['image/jpeg', 'image/png', 'application/pdf'];
        if (!validTypes.includes(file.type)) {
            setFileError('Only JPG, PNG, and PDF files are allowed');
            return;
        }

        setSelectedFile(file);
        setFileError('');
    };

    // In the handleAddAttachment function in GuestForm component
    const handleAddAttachment = async () => {
        if (!selectedDocumentType || !selectedFile) {
            setFileError('Please select both document type and file');
            return;
        }

        showLoader(true);
        try {
            // Step 1: Upload File
            const formData = new FormData();
            formData.append('file', selectedFile);
            formData.append('folder', 'Home/Attachments');
            formData.append('is_private', '1');

            const uploadResponse = await uploadFrappeMedia(formData);
            if (!uploadResponse.data?.message) {
                throw new Error('File upload failed');
            }

            const uploadedFile = uploadResponse.data.message;



            // Step 3: Create tag params locally here
            const params = new URLSearchParams();
            params.append('tag', selectedDocumentType);
            params.append('dt', 'File');
            params.append('dn', uploadedFile.name);

            try {
                await addTag(params);
            } catch (tagError) {
                console.warn('Tagging failed:', tagError);
                showNotification('warning', 'Warning', 'File uploaded but tagging failed');
            }

            // Step 4: Add to attachments
            const newAttachment = {
                type: selectedDocumentType,
                fileUrl: uploadedFile.file_url,
                fileName: uploadedFile.file_name,
                fileType: uploadedFile.file_type,
                fileSize: uploadedFile.file_size,
                format: selectedFile.type.split('/').pop().toUpperCase(),
                name: selectedFile.name,
                fileId: uploadedFile.name,
                ...uploadedFile
            };

            const updatedAttachments = [...attachments, newAttachment];
            setAttachments(updatedAttachments);
            onChange("attachments", updatedAttachments);

            showNotification('success', 'Success', 'File uploaded and tagged successfully');
            handleCloseAttachmentPopup();

        } catch (error) {
            console.error('Attachment error:', error);
            setFileError('Failed to complete attachment process. Please try again.');
            showNotification('error', 'Error', error.message || 'Something went wrong');
        } finally {
            hideLoader();
        }
    };

    // Fetch countries
    const fetchCountries = async () => {
        try {
            showLoader(true);
            const Params = {
                doctype: 'Country',
                fields: JSON.stringify(["country_name"])
            };

            const response = await fetchCountryList(Params);
            const transformedCountries = response.data.message.values.map(([country_name]) => ({
                name: country_name
            }));

            setCountries(transformedCountries);
        } catch (error) {
            console.error('Error fetching countries:', error);
            showNotification('error', 'Error', 'Failed to fetch countries');
        } finally {
            hideLoader();
        }
    };

    // Fetch states based on country using CountriesNow API
    const fetchStates = async (countryName) => {
        if (!countryName) {
            setStates([]);
            return;
        }

        try {
            showLoader(true);

            const response = await fetchStateList(countryName);

            // Handle the CountriesNow API response structure
            if (response.data && response.data.data && response.data.data.states) {
                const transformedStates = response.data.data.states.map(state => ({
                    name: state.name
                }));
                setStates(transformedStates);
            } else {
                setStates([]);
                showNotification('warning', 'Warning', 'No states found for this country');
            }
        } catch (error) {
            console.error('Error fetching states:', error);
            showNotification('error', 'Error', 'Failed to fetch states');
            setStates([]);
        } finally {
            hideLoader();
        }
    };
    useEffect(() => {
        fetchCountries();
    }, []);

    // Fetch states when country changes
    useEffect(() => {
        if (values.country) {
            fetchStates(values.country);
        } else {
            setStates([]);
        }
    }, [values.country]);

    const handleChange = (field, value) => {
        // If country is being changed, clear the state field
        if (field === 'country') {
            onChange(field, value);
            onChange('state', ''); // Clear state when country changes
        } else {
            onChange(field, value);
        }

        // Existing validation logic
        if (field === 'mobile') {
            setErrors(prev => ({ ...prev, mobile: !isValidInternationalPhone(value) }));
            if (onMobileNumberChange && value.length >= 10) {
                onMobileNumberChange(value);
            }
        } else if (field === 'email') {
            setErrors(prev => ({ ...prev, email: !isValidEmail(value) }));
            if (onEmailChange && isValidEmail(value)) {
                onEmailChange(value);
            }
        }
    };
    const isValidInternationalPhone = (phone) => /^\+?[1-9]\d{1,14}$/.test(phone.trim());
    const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

    const [errors, setErrors] = React.useState({
        mobile: false,
        email: false
    });

    const hasName = Boolean((values.firstName || '').trim() || (values.lastName || '').trim());
    const isNameMissing = !hasName;
    const isMobileMissing = isPrimary && !(values.mobile || '').trim();
    const isEmailMissing = isPrimary && !(values.email || '').trim();
    // Attachments are optional

    const FieldLabel = ({ children, required, optionalLabel = "Optional" }) => (
        <Typography variant="bodyText1" sx={{ color: "dark.main", marginBottom: 0.5 }}>
            {children}
            {required ? (
                <Box component="span" sx={{ color: "error.main" }}> *</Box>
            ) : (
                <Box component="span" sx={{ color: "text.secondary", ml: 0.5 }}>({optionalLabel})</Box>
            )}
        </Typography>
    );



    const ReadOnlyBox = ({ children }) => {
        const text =
            typeof children === 'string' && children.length > 20
                ? `${children.slice(0, 20)}...`
                : children;

        return (
            <Box
                sx={{
                    backgroundColor: theme.palette.lightgray.main,
                    border: `1px solid ${theme.palette.grey[300]}`,
                    borderRadius: theme.borderRadius.medium,
                    p: 1.5,
                    minHeight: '36px',
                    display: 'flex',
                    alignItems: 'center',
                    boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.05)',
                }}
            >
                <Typography sx={{ color: theme.palette.text.primary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {text}
                </Typography>
            </Box>
        );
    };

    return (

        <Box sx={{
            backgroundColor: theme.palette.light.main,
            borderRadius: theme.borderRadius.xl,
            border: "1px solid var(--Gray, #CFCFCF)",
            p: 1.2,
            width: '100%',
            mt: 0
        }}>
            <Dialog open={attachmentPopupOpen} onClose={handleCloseAttachmentPopup}>
                <DialogTitle>Attachments</DialogTitle>
                <DialogContent>
                    <Box sx={{ minWidth: 400, p: 2 }}>
                        <Typography variant="body1" sx={{ mb: 2 }}>
                            Select document type and upload file
                        </Typography>

                        <StyledAutocomplete
                            options={idProofs}
                            value={selectedDocumentType}
                            onChange={(event, value) => setSelectedDocumentType(value)}
                            placeholder="Select document type"
                        />

                        <input
                            accept=".jpg,.jpeg,.png,.pdf"
                            style={{ display: 'none' }}
                            id="raised-button-file"
                            type="file"
                            onChange={handleFileChange}

                        />
                        <label htmlFor="raised-button-file">
                            <Button variant="contained" component="span" fullWidth sx={{ mt: 2 }}>
                                Choose File
                            </Button>
                        </label>

                        {selectedFile && (
                            <Typography variant="body2" sx={{ mt: 1 }}>
                                Selected: {selectedFile.name} ({Math.round(selectedFile.size / 1024)} KB)
                            </Typography>
                        )}

                        {fileError && (
                            <Typography color="error" variant="body2" sx={{ mt: 1 }}>
                                {fileError}
                            </Typography>
                        )}
                    </Box>
                </DialogContent>
                <DialogActions sx={{ mb: 2 }}>

                    <Button onClick={handleCloseAttachmentPopup} variant="outlined"
                        sx={{
                            width: 130,
                            height: 36,
                            borderRadius: theme.borderRadius.medium,
                            borderColor: theme.palette.gray.main,
                            color: theme.palette.dark.main,
                            py: 1.5,
                            "&:hover": {
                                borderColor: theme.palette.green.main
                            }
                        }}>Cancel
                    </Button>
                    <Button
                        onClick={handleAddAttachment}
                        disabled={!selectedDocumentType || !selectedFile}
                        sx={{
                            borderRadius: theme.borderRadius.medium,
                            backgroundColor: 'primary.main',
                            color: theme.palette.light.main,
                            py: 1.5,
                            minWidth: 180,
                            height: 36,
                            "&:hover": {
                                backgroundColor: theme.palette.green.dark
                            },
                            "&:disabled": {
                                backgroundColor: theme.palette.gray.main,
                                color: theme.palette.dark.main
                            }
                        }}

                    >
                        Add Attachment
                    </Button>


                </DialogActions>
            </Dialog>
            <Grid container spacing={1.5}>
                {/* Mobile and Email (Primary guest gets priority placement) */}
                {isPrimary && (
                    <>
                        <Grid size={6}>
                            <FieldLabel required={isPrimary}>Mobile</FieldLabel>
                            {isEditable ? (
                                <StyledTextField
                                    fullWidth
                                    placeholder='Mobile Number'
                                    value={values.mobile}
                                    onChange={(e) => {
                                        const newValue = e.target.value;
                                        handleChange("mobile", newValue);

                                        if (newValue.length === 10) {
                                            onMobileNumberChange(newValue);
                                        } else if (newValue.length < 10) {
                                            onMobileNumberChange(newValue);
                                        }
                                    }}
                                    error={errors.mobile || (showRequiredErrors && isMobileMissing)}
                                    helperText={
                                        showRequiredErrors && isMobileMissing
                                            ? "Mobile is required for the primary guest"
                                            : errors.mobile
                                                ? "Enter a valid mobile number"
                                                : ""
                                    }
                                    InputProps={{
                                        endAdornment: errors.mobile && (
                                            <InputAdornment position="end">
                                                <ErrorIcon color="error" />
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            ) : (
                                <ReadOnlyBox>
                                    <Typography>{values.mobile || '-'}</Typography>
                                </ReadOnlyBox>
                            )}
                        </Grid>

                        <Grid size={6}>
                            <FieldLabel required={isPrimary}>E-mail</FieldLabel>
                            {isEditable ? (
                                <StyledTextField
                                    fullWidth
                                    placeholder='Enter Email ID'
                                    value={values.email}
                                    onChange={(e) => {
                                        const newValue = e.target.value;
                                        handleChange("email", newValue);
                                        if (isValidEmail(newValue)) {
                                            onEmailChange(newValue);
                                        }
                                    }}
                                    error={errors.email || (showRequiredErrors && isEmailMissing)}
                                    helperText={
                                        showRequiredErrors && isEmailMissing
                                            ? "Email is required for the primary guest"
                                            : errors.email
                                                ? "Enter a valid email address"
                                                : ""
                                    }
                                    InputProps={{
                                        endAdornment: errors.email && (
                                            <InputAdornment position="end">
                                                <ErrorIcon color="error" />
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            ) : (
                                <ReadOnlyBox>
                                    <Typography>{values.email || '-'}</Typography>
                                </ReadOnlyBox>
                            )}
                        </Grid>
                    </>
                )}
                {/* Title, First Name, Last Name */}
                <Grid size={4}>
                    <Typography variant="bodyText1" sx={{ color: "dark.main", marginBottom: 0.5 }}>
                        Title
                    </Typography>
                    {isEditable ? (
                        <StyledAutocomplete
                            options={titles}
                            value={values.title}
                            onChange={(event, value) => handleChange("title", value)}
                            placeholder="Mr."
                        />
                    ) : (
                        <ReadOnlyBox>
                            <Typography>{values.title || '-'}</Typography>
                        </ReadOnlyBox>
                    )}
                </Grid>
                <Grid size={8}>
                    <FieldLabel required={true} optionalLabel="">
                        First Name
                    </FieldLabel>
                    {isEditable ? (
                        <StyledTextField
                            fullWidth
                            placeholder='Enter First Name'
                            value={values.firstName}
                            onChange={(e) => handleChange("firstName", e.target.value)}
                            error={showRequiredErrors && isNameMissing}
                            helperText={showRequiredErrors && isNameMissing ? "First or last name is required" : ""}
                        />
                    ) : (
                        <ReadOnlyBox>
                            <Typography>{values.firstName || '-'}</Typography>
                        </ReadOnlyBox>
                    )}
                </Grid>
                <Grid size={12}>
                    <FieldLabel required={true} optionalLabel="">
                        Last Name
                    </FieldLabel>
                    {isEditable ? (
                        <StyledTextField
                            fullWidth
                            placeholder='Enter Last Name'
                            value={values.lastName}
                            onChange={(e) => handleChange("lastName", e.target.value)}
                            error={showRequiredErrors && isNameMissing}
                            helperText={showRequiredErrors && isNameMissing ? "First or last name is required" : ""}
                        />
                    ) : (
                        <ReadOnlyBox>
                            <Typography>{values.lastName || '-'}</Typography>
                        </ReadOnlyBox>
                    )}
                </Grid>

                {/* Attachments - high priority */}
                {isEditable && (
                    <Grid size={12}>
                        <Typography
                            variant="body2"
                            color="secondary"
                            fontWeight={500}
                            sx={{ cursor: 'pointer' }}
                            onClick={handleOpenAttachmentPopup}
                        >
                            <AttachmentIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                            Add Attachment
                        </Typography>
                    </Grid>
                )}
                <Grid size={12}>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                        {attachments.map((attachment, index) => (
                            <Chip
                                key={index}
                                label={`${attachment.type} (${attachment.format})`}
                                // onDelete={() => { }}
                                onClick={async () => {
                                    try {
                                        let firesp = await retrievefile(attachment.file_url);
                                        const file = new Blob([firesp.data], { type: firesp.headers['content-type'] });
                                        const fileURL = URL.createObjectURL(file);
                                        window.open(fileURL);
                                    } catch (err) {
                                        console.log(err);
                                    }
                                }}
                                sx={{
                                    backgroundColor: theme.palette.secondary.main,
                                    color: theme.palette.primary.main
                                }}
                            />
                        ))}
                    </Box>
                </Grid>

                {/* Gender & Nationality */}
                <Grid size={6}>
                    <Typography variant="bodyText1" sx={{ color: "dark.main", marginBottom: 0.5 }}>
                        Gender
                    </Typography>
                    {isEditable ? (
                        <StyledAutocomplete
                            options={genders}
                            value={values.gender}
                            onChange={(event, value) => handleChange("gender", value)}
                            placeholder='Select'
                        />
                    ) : (
                        <ReadOnlyBox>
                            <Typography>{values.gender || '-'}</Typography>
                        </ReadOnlyBox>
                    )}
                </Grid>
                <Grid size={6}>
                    <Typography variant="bodyText1" sx={{ color: "dark.main", marginBottom: 0.5 }}>
                        Nationality
                    </Typography>
                    {isEditable ? (
                        <StyledTextField
                            fullWidth
                            value={values.nationality}
                            onChange={(event) => {
                                const value = event.target.value;
                                const filteredValue = value.replace(/[^a-zA-Z\s]/g, '');
                                handleChange("nationality", filteredValue);
                            }}
                            placeholder="Enter Nationality"
                        />
                    ) : (
                        <ReadOnlyBox>
                            <Typography>{values.nationality || '-'}</Typography>
                        </ReadOnlyBox>
                    )}
                </Grid>

                {/* ID Proof & Number */}
                <Grid size={6}>
                    <Typography variant="bodyText1" sx={{ color: "dark.main", marginBottom: 0.5 }}>
                        ID Proof Type
                    </Typography>
                    {isEditable ? (
                        <StyledAutocomplete
                            options={idProofs}
                            value={values.idProofType}  // Changed from id_proof_number
                            onChange={(event, value) => handleChange("idProofType", value)}
                            placeholder="select"
                        />
                    ) : (
                        <ReadOnlyBox>
                            <Typography>{values.idProofType || '-'}</Typography>
                        </ReadOnlyBox>
                    )}
                </Grid>
                <Grid size={6}>
                    <Typography variant="bodyText1" sx={{ color: "dark.main", marginBottom: 0.5 }}>
                        ID Number
                    </Typography>
                    {isEditable ? (
                        <StyledTextField
                            fullWidth
                            placeholder='Enter ID Number'
                            value={values.idNumber}
                            onChange={(e) => handleChange("idNumber", e.target.value)}
                        />
                    ) : (
                        <ReadOnlyBox>
                            <Typography>{values.idNumber || '-'}</Typography>
                        </ReadOnlyBox>
                    )}
                </Grid>

                {/* Address (Primary guest only) */}
                {isPrimary && (
                    <>
                        <Grid size={12} mt={-2.5}>
                            <Typography variant="bodyText1" sx={{ color: "dark.main", marginBottom: 0.5 }}>
                                Address
                            </Typography>
                            {isEditable ? (
                                <StyledTextField
                                    fullWidth
                                    placeholder='Enter Address'
                                    value={values.address}
                                    onChange={(e) => handleChange("address", e.target.value)}
                                />
                            ) : (
                                <ReadOnlyBox>
                                    <Typography>{values.address || '-'}</Typography>
                                </ReadOnlyBox>
                            )}
                        </Grid>

                        <Grid size={6}>
                            <Typography variant="bodyText1" sx={{ color: "dark.main", marginBottom: 0.5 }}>
                                Country
                            </Typography>
                            {isEditable ? (
                                <StyledAutocomplete
                                    options={countries}
                                    getOptionLabel={(option) => option?.name || ""}
                                    value={countries.find(c => c.name === values.country) || null}
                                    onChange={(event, value) => handleChange("country", value?.name || "")}
                                    placeholder="Select Country"
                                />
                            ) : (
                                <ReadOnlyBox>
                                    <Typography>{values.country || '-'}</Typography>
                                </ReadOnlyBox>
                            )}
                        </Grid>

                        {/* State Field - Updated to use Autocomplete */}
                        <Grid size={6}>
                            <Typography variant="bodyText1" sx={{ color: "dark.main", marginBottom: 0.5 }}>
                                State
                            </Typography>
                            {isEditable ? (
                                <StyledAutocomplete
                                    options={states}
                                    getOptionLabel={(option) => option?.name || ""}
                                    value={states.find(s => s.name === values.state) || null}
                                    onChange={(event, value) => handleChange("state", value?.name || "")}
                                    placeholder={values.country ? "Select State" : "Select Country First"}
                                    disabled={!values.country} // Disable if no country selected
                                />
                            ) : (
                                <ReadOnlyBox>
                                    <Typography>{values.state || '-'}</Typography>
                                </ReadOnlyBox>
                            )}
                        </Grid>

                        {/* City & Pin code */}
                        <Grid size={6}>
                            <Typography variant="bodyText1" sx={{ color: "dark.main", marginBottom: 0.5 }}>
                                City
                            </Typography>
                            {isEditable ? (
                                <StyledTextField
                                    value={values.city}
                                    onChange={(event) => {
                                        const input = event.target.value;
                                        const filteredValue = input.replace(/[^a-zA-Z\s]/g, '');
                                        handleChange("city", filteredValue);
                                    }}
                                    placeholder="Enter City"
                                />
                            ) : (
                                <ReadOnlyBox>
                                    <Typography>{values.city || '-'}</Typography>
                                </ReadOnlyBox>
                            )}
                        </Grid>
                        <Grid size={6}>
                            <Typography variant="bodyText1" sx={{ color: "dark.main", marginBottom: 0.5 }}>
                                Pin Code
                            </Typography>
                            {isEditable ? (
                                <StyledTextField
                                    fullWidth
                                    value={values.pinCode}
                                    onChange={(e) => {
                                        const numericValue = e.target.value.replace(/\D/g, '');
                                        handleChange("pinCode", numericValue);
                                    }}
                                    placeholder="Enter Pin Code"
                                />
                            ) : (
                                <ReadOnlyBox>
                                    <Typography>{values.pinCode || '-'}</Typography>
                                </ReadOnlyBox>
                            )}
                        </Grid>
                    </>
                )}

                {/* Mobile and Email (Additional guests go to bottom) */}
                {!isPrimary && (
                    <>
                        <Grid size={6}>
                            <FieldLabel required={isPrimary}>Mobile</FieldLabel>
                            {isEditable ? (
                                <StyledTextField
                                    fullWidth
                                    placeholder='Mobile Number'
                                    value={values.mobile}
                                    onChange={(e) => {
                                        const newValue = e.target.value;
                                        handleChange("mobile", newValue);

                                        if (newValue.length === 10) {
                                            onMobileNumberChange(newValue);
                                        } else if (newValue.length < 10) {
                                            onMobileNumberChange(newValue);
                                        }
                                    }}
                                    error={errors.mobile || (showRequiredErrors && isMobileMissing)}
                                    helperText={
                                        showRequiredErrors && isMobileMissing
                                            ? "Mobile is required for the primary guest"
                                            : errors.mobile
                                                ? "Enter a valid mobile number"
                                                : ""
                                    }
                                    InputProps={{
                                        endAdornment: errors.mobile && (
                                            <InputAdornment position="end">
                                                <ErrorIcon color="error" />
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            ) : (
                                <ReadOnlyBox>
                                    <Typography>{values.mobile || '-'}</Typography>
                                </ReadOnlyBox>
                            )}
                        </Grid>

                        <Grid size={6}>
                            <FieldLabel required={isPrimary}>E-mail</FieldLabel>
                            {isEditable ? (
                                <StyledTextField
                                    fullWidth
                                    placeholder='Enter Email ID'
                                    value={values.email}
                                    onChange={(e) => {
                                        const newValue = e.target.value;
                                        handleChange("email", newValue);
                                        if (isValidEmail(newValue)) {
                                            onEmailChange(newValue);
                                        }
                                    }}
                                    error={errors.email || (showRequiredErrors && isEmailMissing)}
                                    helperText={
                                        showRequiredErrors && isEmailMissing
                                            ? "Email is required for the primary guest"
                                            : errors.email
                                                ? "Enter a valid email address"
                                                : ""
                                    }
                                    InputProps={{
                                        endAdornment: errors.email && (
                                            <InputAdornment position="end">
                                                <ErrorIcon color="error" />
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            ) : (
                                <ReadOnlyBox>
                                    <Typography>{values.email || '-'}</Typography>
                                </ReadOnlyBox>
                            )}
                        </Grid>
                    </>
                )}
            </Grid>
        </Box>
    );
});

export default function GuestDetails({ guests, onAddGuest, isEditMode, onGuestChange, }) {
    // In your GuestForm component
    const [formValues, setFormValues] = useState({
        guest_name: '',
        mobile: '',
        email: '',
        title: '',
        firstName: '',
        lastName: '',
        gender: '',
        nationality: '',
        idProofType: '',
        idNumber: '',
        address: '',
        country: '',
        state: '',
        city: '',
        pinCode: '',
        attachments: []  // Add this line
    });

    // Set first guest as expanded by default if guests exist
    const [expandedGuestId, setExpandedGuestId] = useState(guests.length > 0 ? guests[0].id : null);
    const [showAddGuest, setShowAddGuest] = useState(!(isEditMode && guests.length > 0));
    const [editingGuestId, setEditingGuestId] = useState(null);
    const [sharePopupOpen, setSharePopupOpen] = useState({
        open: false,
        details: null
    });
    const { showLoader, hideLoader } = useLoading();
    const { showNotification } = useNotification();
    const [attachments, setAttachments] = useState();
    const [showAddValidation, setShowAddValidation] = useState(false);

    useEffect(() => {
        if (isEditMode && guests.length > 0) {
            setShowAddGuest(false);
            setExpandedGuestId(guests[0].id);
        }
    }, [isEditMode, guests]);


    const handleChange = (field, value) => {
        setFormValues(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const isValidInternationalPhone = (phone) => /^\+?[1-9]\d{1,14}$/.test((phone || '').trim());
    const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((email || '').trim());

    const validateGuest = (values, isPrimaryGuest) => {
        const missing = [];
        const hasName = Boolean((values.firstName || '').trim() || (values.lastName || '').trim());
        if (!hasName) {
            missing.push('name');
        }
        if (isPrimaryGuest) {
            if (!values.mobile || !values.mobile.trim()) {
                missing.push('mobile');
            } else if (!isValidInternationalPhone(values.mobile)) {
                missing.push('valid mobile');
            }
            if (!values.email || !values.email.trim()) {
                missing.push('email');
            } else if (!isValidEmail(values.email)) {
                missing.push('valid email');
            }
        }
        return {
            valid: missing.length === 0,
            missing
        };
    };

    const buildValidationMessage = (missing, isPrimaryGuest) => {
        if (!missing.length) return '';
        const friendly = missing.includes('name') ? 'name' : null;
        const mobileMissing = missing.includes('mobile') || missing.includes('valid mobile');
        const emailMissing = missing.includes('email') || missing.includes('valid email');

        const parts = [];
        if (friendly) parts.push('name');
        if (isPrimaryGuest && (mobileMissing || emailMissing)) {
            const primaryParts = [];
            if (mobileMissing) primaryParts.push('mobile');
            if (emailMissing) primaryParts.push('email');
            parts.push(`primary guest ${primaryParts.join(' & ')}`);
        }
        return `Please complete required details: ${parts.join(', ')}.`;
    };

    const handleAddNewGuest = () => {
        const isPrimaryGuest = guests.length === 0;
        const validation = validateGuest(formValues, isPrimaryGuest);
        if (!validation.valid) {
            setShowAddValidation(true);
            showNotification('error', 'Missing Details', buildValidationMessage(validation.missing, isPrimaryGuest));
            return;
        }

        onAddGuest({
            ...formValues,
            id: Date.now()
        });
        setFormValues({
            mobile: '',
            email: '',
            title: '',
            firstName: '',
            lastName: '',
            gender: '',
            nationality: '',
            idProofType: '',
            idNumber: '',
            address: '',
            country: '',
            state: '',
            city: '',
            pinCode: '',
            attachments: [],
            guest_name: ''
        });
        setShowAddGuest(false);
        setShowAddValidation(false);
    };

    const mobileNumberTimeoutRef = useRef(null);
    const emailTimeoutRef = useRef(null);
    const MIN_MOBILE_LENGTH = 8;

    const clearGuestFields = useCallback((preserveField = null) => {
        setFormValues(prev => {
            const newValues = {
                ...prev,
                title: '',
                firstName: '',
                lastName: '',
                gender: '',
                nationality: '',
                idProofType: '',
                idNumber: '',
                address: '',
                country: '',
                state: '',
                city: '',
                pinCode: '',
                attachments: []
            };

            // Preserve either mobile or email if specified
            if (preserveField === 'mobile') {
                newValues.mobile = prev.mobile;
            } else if (preserveField === 'email') {
                newValues.email = prev.email;
            }

            return newValues;
        });
        setAttachments([]);
    }, []);

    const fetchGuestDetails = useCallback(async (identifier, isEmail = false) => {
        try {
            showLoader(true);
            const fields = [
                "name", "title", "first_name", "last_name",
                "mobile_number", "email", "gender",
                "id_proof", "id_proof_number", "country", "state", "city",
                "pin_code", "nationality", "address"
            ];

            // Create appropriate filter based on whether we're searching by email or mobile
            const filter = isEmail
                ? [["email", "=", identifier]]
                : [["mobile_number", "=", identifier]];

            const response = await fetchGuestDetailsByMobile({
                doctype: "Guest Details",
                fields: JSON.stringify(fields),
                filters: JSON.stringify(filter)
            });

            const values = response.data.message?.values || [];

            if (values.length > 0) {
                const guestData = values[0];

                let docinfogst = await getDocInfoComplete({
                    doctype: "Guest Details",
                    name: guestData[0]
                });

                let attchs = docinfogst.data.docinfo?.attachments || [];
                let guest_attachments = [];

                for (let atc of attchs) {
                    let deets = await getFileDetails(atc.name);
                    if (deets.data.data) {
                        guest_attachments.push({
                            ...deets.data.data,
                            type: deets.data.data._user_tags?.split(",")[1],
                            fileUrl: deets.data.data.file_url,
                            fileName: deets.data.data.file_name,
                            fileType: deets.data.data.file_type,
                            fileSize: deets.data.data.file_size,
                            format: deets.data.data.file_type,
                            fileId: deets.data.data.name,
                        });
                    }
                }

                setFormValues(prev => ({
                    ...prev,
                    guest_name: guestData[0],
                    title: guestData[1] || '',
                    firstName: guestData[2] || '',
                    lastName: guestData[3] || '',
                    mobile: guestData[4] || '', // Set mobile from response
                    email: guestData[5] || '',  // Set email from response
                    gender: guestData[6] || '',
                    idProofType: guestData[7] || '',
                    idNumber: guestData[8] || '',
                    country: guestData[9] || '',
                    state: guestData[10] || '',
                    city: guestData[11] || '',
                    pinCode: guestData[12] || '',
                    nationality: guestData[13] || '',
                    address: guestData[14] || '',
                    attachments: guest_attachments
                }));
                showNotification('success', 'Success', 'Guest details loaded');
            } else {
                clearGuestFields();
            }
        } catch (error) {
            console.error('Error fetching guest details:', error);
            showNotification('error', 'Error', 'Failed to fetch guest details');
        } finally {
            hideLoader();
        }
    }, [showLoader, hideLoader, showNotification, clearGuestFields]);

    const handleMobileNumberChange = useCallback((mobileNumber) => {
        if (mobileNumberTimeoutRef.current) {
            clearTimeout(mobileNumberTimeoutRef.current);
        }

        if (mobileNumber.length < MIN_MOBILE_LENGTH) {
            clearGuestFields();
            return;
        }

        mobileNumberTimeoutRef.current = setTimeout(() => {
            fetchGuestDetails(mobileNumber, false);
        }, 500);
    }, [fetchGuestDetails, clearGuestFields]);

    const handleEmailChange = useCallback((email) => {
        if (emailTimeoutRef.current) {
            clearTimeout(emailTimeoutRef.current);
        }

        if (!email || email.length < 5) { // Basic email length check
            // Clear fields if email is being deleted or not valid
            if (formValues.email && email.length < formValues.email.length) {
                clearGuestFields();
            }
            return;
        }

        emailTimeoutRef.current = setTimeout(() => {
            // Only fetch if this is a new email (not matching existing value)
            if (email !== formValues.email) {
                fetchGuestDetails(email, true);
            }
        }, 500);
    }, [fetchGuestDetails, formValues.email]);
    useEffect(() => {
        return () => {
            if (mobileNumberTimeoutRef.current) {
                clearTimeout(mobileNumberTimeoutRef.current);
            }
            if (emailTimeoutRef.current) {
                clearTimeout(emailTimeoutRef.current);
            }
        };
    }, []);
    const toggleExpandGuest = (id) => {
        if (expandedGuestId === id) {
            setExpandedGuestId(null);
        } else {
            setExpandedGuestId(id);
        }
        setEditingGuestId(null);
        setShowAddGuest(false);
    };

    const handleEditGuest = (id) => {
        setEditingGuestId(id);
        setShowAddGuest(false);
    };

    const handleCancelEdit = () => {
        setEditingGuestId(null);
    };

    const handleOpenSharePopup = () => {
        setSharePopupOpen({ open: true, details: null });
    };

    const handleCloseSharePopup = () => {
        setSharePopupOpen({ open: false, details: null });
    };

    const handleShowAddGuest = () => {
        setShowAddGuest(true);
        setExpandedGuestId(null);
        setEditingGuestId(null);
        setShowAddValidation(false);
    };

    return (
        <>
            {/* Attachment Popup */}

            <Box
                sx={{
                    backgroundColor: theme.palette.light.main,
                    borderRadius: theme.borderRadius.xl,
                    boxShadow: '0px 4px 12px 0px rgba(74, 76, 78, 0.25)',
                    p: 2.5,
                    width: '100%',
                    maxWidth: 650,
                    maxHeight: "fit-content"
                }}
            >
                {guests.length > 0 && (
                    <Box sx={{ mb: 3 }}>
                        <Typography variant="SubTitleBold" mb={1}>
                            Guest Details
                        </Typography>
                        <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mb: 2 }}>
                            Name is required for all guests. Mobile and email are required only for the primary guest. Attachments are optional.
                        </Typography>
                        {guests.map((guest, index) => (
                            <Box key={guest.id} sx={{ mb: 2 }}>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        backgroundColor: theme.palette.lightgray.main,
                                        borderRadius: theme.borderRadius.medium,
                                        p: 1.5,
                                        cursor: 'pointer'
                                    }}
                                    onClick={() => toggleExpandGuest(guest.id)}
                                >
                                    <Typography variant="body1">
                                        {`${guest.title || ''} ${guest.firstName || ''} ${guest.lastName || ''}`.trim() || `Guest ${index + 1}`}
                                    </Typography>
                                    <Box display="flex" alignItems="center">
                                        <Chip
                                            size="small"
                                            label={index === 0 ? "Primary" : "Additional"}
                                            sx={{ mr: 1, backgroundColor: theme.palette.light.main }}
                                        />
                                        {expandedGuestId === guest.id && isEditMode && (
                                            <Button
                                                variant="text"
                                                size="small"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleEditGuest(guest.id);
                                                }}
                                                sx={{ mr: 1 }}
                                            >
                                                Edit
                                            </Button>
                                        )}
                                        <IconButton size="small" onClick={(e) => {
                                            e.stopPropagation();
                                            toggleExpandGuest(guest.id);
                                        }}>
                                            {expandedGuestId === guest.id ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                        </IconButton>
                                    </Box>
                                </Box>

                                <Collapse in={expandedGuestId === guest.id}>
                                    <GuestForm
                                        values={{
                                            ...guest,
                                            guest_name: guest.guest_name || guest.id
                                        }}
                                        isEditable={editingGuestId === guest.id}
                                        isPrimary={index === 0}
                                        onMobileNumberChange={handleMobileNumberChange}
                                        onEmailChange={handleEmailChange}
                                        onChange={(field, value) => {
                                            if (onGuestChange) {
                                                onGuestChange(guest.id, field, value);
                                            }
                                        }}
                                    />
                                </Collapse>
                            </Box>
                        ))}
                    </Box>
                )}

                {showAddGuest && (
                    <>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="SubTitleBold" mt={-1.5}>
                                {guests.length === 0 ? "Add Primary Guest" : "Add Additional Guest"}
                            </Typography>
                            {isEditMode && (
                                <IconButton onClick={() => setShowAddGuest(false)}>
                                    <CloseIcon />
                                </IconButton>
                            )}
                        </Box>
                        <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mb: 1 }}>
                            Name is required for all guests. Mobile and email are required only for the primary guest. Attachments are optional.
                        </Typography>

                        <GuestForm
                            values={formValues}
                            onChange={handleChange}
                            isEditable={true}
                            onMobileNumberChange={handleMobileNumberChange}
                            onEmailChange={handleEmailChange}
                            isPrimary={guests.length === 0}
                            showRequiredErrors={showAddValidation}

                        />
                        <Box
                            sx={{
                                display: "flex",
                                justifyContent: "flex-start",
                                backgroundColor: theme.palette.light.main,
                                mt: 0.5,
                                width: '100%',
                                maxWidth: "550px",
                            }}
                        >
                            <Button
                                variant="text"
                                startIcon={<SaveIcon />}
                                color="primary"
                                onClick={handleAddNewGuest}
                            >
                                <Typography variant='subHeading' sx={{ textTransform: "none" }}>  Save Guest</Typography>
                            </Button>
                        </Box>
                    </>
                )}

                {!showAddGuest && (
                    <Button
                        variant="text"
                        color="primary"
                        startIcon={<AddIcon />}
                        onClick={handleShowAddGuest}
                        sx={{ mt: -2 }}
                    >
                        Add Guest
                    </Button>
                )}
            </Box>

            {/* <Box mt={2}>
                <img
                    src={share_Main}
                    alt="Share"
                    style={{ cursor: "pointer" }}
                    onClick={handleOpenSharePopup}
                />
            </Box>

            <ShareDetailsPopup
                open={sharePopupOpen.open}
                onClose={handleCloseSharePopup}
            /> */}
        </>
    );
}
